import * as React from 'react';

type BreakpointConfig = {
    mobile: number;
    tablet: number;
    desktop: number;
};

type DeviceType = 'mobile' | 'tablet' | 'desktop';

const DEFAULT_BREAKPOINTS: BreakpointConfig = {
    mobile: 768,
    tablet: 1024,
    desktop: 1200,
};

export const useResponsive = (customBreakpoints?: Partial<BreakpointConfig>) => {
    const breakpoints = { ...DEFAULT_BREAKPOINTS, ...customBreakpoints };

    const [screenSize, setScreenSize] = React.useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    React.useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = screenSize.width < breakpoints.mobile;
    const isTablet =
        screenSize.width >= breakpoints.mobile && screenSize.width < breakpoints.tablet;
    const isDesktop = screenSize.width >= breakpoints.desktop;
    const isSmallScreen = screenSize.width < breakpoints.tablet;

    const deviceType: DeviceType = React.useMemo(() => {
        if (isMobile) {
            return 'mobile';
        }
        if (isTablet) {
            return 'tablet';
        }

        return 'desktop';
    }, [isMobile, isTablet]);

    // Helper pour obtenir une valeur selon le device
    const getResponsiveValue = <T>(values: { mobile?: T; tablet?: T; desktop: T }): T => {
        if (isMobile && values.mobile !== undefined) return values.mobile;
        if (isTablet && values.tablet !== undefined) return values.tablet;
        return values.desktop;
    };

    return {
        screenSize,
        isMobile,
        isTablet,
        isDesktop,
        isSmallScreen,
        deviceType,
        getResponsiveValue,
        breakpoints,
    };
};
